/** `/lotteries`: list with status badges, filters and Appendix A lifecycle actions per row. */
import { useQuery } from '@tanstack/react-query';
import { LotteryMechanic, LotteryStatus, type LotteryListRowDto } from '@windrop/contracts';
import { Button, useIntl } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Can } from '../../auth/guards';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, humanizeStatus } from '../../components/StatusBadge';
import { lotteriesApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';
import { LifecycleActions } from './lifecycle';

export function LotteriesListPage() {
  const intl = useIntl();
  const [status, setStatus] = useState<string>('');
  const [mechanic, setMechanic] = useState<string>('');
  const query = useQuery({
    queryKey: ['lotteries', { status, mechanic }],
    queryFn: () =>
      lotteriesApi.list({
        status: status ? (status as LotteryStatus) : undefined,
        mechanic: mechanic ? (mechanic as LotteryMechanic) : undefined,
      }),
  });

  const columns = useMemo<Column<LotteryListRowDto>[]>(
    () => [
      {
        id: 'title',
        header: 'Lottery',
        sortValue: (r) => r.title.toLowerCase(),
        filterValue: (r) => `${r.title} ${r.slug}`,
        cell: (r) => (
          <>
            <Link to={`/lotteries/${r.lotteryId}`}>
              <strong>{r.title}</strong>
            </Link>
            <br />
            <code className="wd-muted">{r.slug}</code>
          </>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortValue: (r) => r.status,
        cell: (r) => <StatusBadge kind="LotteryStatus" value={r.status} />,
      },
      {
        id: 'mechanic',
        header: 'Mechanic',
        sortValue: (r) => r.mechanic,
        cell: (r) => humanizeStatus(r.mechanic),
      },
      {
        id: 'opensAt',
        header: 'Opens',
        sortValue: (r) => r.opensAt,
        cell: (r) => intl.formatDateTime(r.opensAt),
      },
      {
        id: 'closesAt',
        header: 'Closes',
        sortValue: (r) => r.closesAt,
        cell: (r) => intl.formatDateTime(r.closesAt),
      },
      {
        id: 'version',
        header: 'Version',
        sortValue: (r) => r.configVersion,
        align: 'end',
        cell: (r) => (
          <span className="wd-tabular">
            v{r.configVersion}
            {r.publishedConfigVersion !== null && r.publishedConfigVersion !== r.configVersion
              ? ` (published v${r.publishedConfigVersion})`
              : ''}
          </span>
        ),
      },
      {
        id: 'entries',
        header: 'Entries',
        cell: (r) => (r.hasEntries ? 'Yes — rules locked' : 'None yet'),
        defaultHidden: true,
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        sortValue: (r) => r.updatedAt,
        cell: (r) => intl.formatRelative(r.updatedAt),
        defaultHidden: true,
      },
    ],
    [intl],
  );

  return (
    <>
      <PageHeader
        title="Lotteries"
        description="Every lottery in this tenant with its Appendix A state. Actions respect your role."
        actions={
          <Can permission="lottery.create">
            <Button href="/lotteries/new">Create lottery</Button>
          </Can>
        }
      />
      <div className="wdc-form-grid" style={{ marginBottom: 'var(--wd-space-4)' }}>
        <label className="wd-field">
          <span className="wd-field__label">Status</span>
          <select className="wd-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {LotteryStatus.options.map((s) => (
              <option key={s} value={s}>
                {humanizeStatus(s)}
              </option>
            ))}
          </select>
        </label>
        <label className="wd-field">
          <span className="wd-field__label">Mechanic</span>
          <select className="wd-select" value={mechanic} onChange={(e) => setMechanic(e.target.value)}>
            <option value="">All mechanics</option>
            {LotteryMechanic.options.map((m) => (
              <option key={m} value={m}>
                {humanizeStatus(m)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {query.isError ? (
        <ErrorState
          title="Could not load lotteries"
          body="Retry, or check the API status if this persists."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable
          caption="Lotteries"
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(r) => r.lotteryId}
          loading={query.isPending}
          initialSort={{ columnId: 'updatedAt', direction: 'desc' }}
          emptyState={
            <EmptyState
              title="No lotteries yet"
              body="Create the first one with the wizard; templates and autosave get you to publish in about ten minutes."
              action={
                <Can permission="lottery.create" fallback={<Button href="/">Back to dashboard</Button>}>
                  <Button href="/lotteries/new">Create lottery</Button>
                </Can>
              }
            />
          }
          rowActions={(r) => (
            <div className="wd-btn-group" style={{ justifyContent: 'flex-end' }}>
              {r.status === 'DRAFT' || r.status === 'REVIEW' || r.status === 'APPROVED' ? (
                <Can permission="lottery.update">
                  <Link to={`/lotteries/${r.lotteryId}/edit`} className="wd-btn" data-variant="quiet">
                    Edit
                  </Link>
                </Can>
              ) : null}
              <LifecycleActions
                lotteryId={r.lotteryId}
                slug={r.slug}
                status={r.status}
                affected={r.hasEntries ? undefined : { affected: 0, detail: 'no entries yet' }}
              />
            </div>
          )}
        />
      )}
    </>
  );
}
